import { LoadingIndicatorComponent } from '../../components/loading-indicator/loading-indicator.component';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import {
  AerosysGatewayService,
  AerosysItemSeparacaoLegado,
  AerosysLoteDisponivel,
  AerosysSerialDisponivel,
  AerosysUsuarioSeparador
} from '../../services/aerosys-gateway.service';

/**
 * ============================================================================
 *  INTEGRAÇÃO GPL — Separação de vendas do Sistema Legado (Aerosys)
 * ============================================================================
 *  Tela de "Continuar Separação" para vendas do Aerosys — mirror simplificado
 *  de EstoqueSeparacaoItensComponent (Sankhya), usado quando o pedido tem
 *  origem === 'aerosys' na tela de Aplicativo de Separação.
 *
 *  Diferenças deliberadas em relação à versão Sankhya:
 *   - SEM validação de posse/dono: qualquer usuário pode continuar e marcar
 *     itens de uma separação já iniciada por outra pessoa (decisão explícita
 *     do cliente).
 *   - SEM os recursos de Dividir/Transferir separação — só a troca manual de
 *     form/serial (ver abaixo), que na versão Sankhya são dois botões/modais
 *     separados (Trocar Serial / Trocar Form), aqui são UM fluxo só: o
 *     separador escolhe primeiro o LOTE de compra (que já define o form) e,
 *     se a peça controla série, escolhe o serial físico dentro daquele MESMO
 *     lote em seguida — nunca form de um lote com serial de outro, porque o
 *     form é o documento de rastreabilidade (ex.: 8130-3) da peça física
 *     daquele lote específico (confirmado com o cliente). O serial/form
 *     inicial continua escolhido automaticamente por FIFO no backend do
 *     Aerosys (ver SeparacaoLegadoService, pacote
 *     com.infoaries.aerosys.gplseparacao) — a troca manual é só pra quando o
 *     separador precisa ajustar essa escolha.
 *     [ATUALIZADO] Isso não é mais verdade — ver "Dividir Separação" abaixo.
 *
 *  IDENTIFICAÇÃO "QUEM SOU VOCÊ": o Aerosys não tem como saber sozinho quem
 *  está usando esta tela, porque toda chamada do GPL-Integracao pro Aerosys
 *  usa um usuário de serviço FIXO (ver AerosysGatewayRepository.autenticar()).
 *  Por isso, antes de carregar os itens, o separador precisa se identificar:
 *  digita uma TAG (crachá físico, texto livre) e escolhe o SEU usuário numa
 *  busca pelos usuários cadastrados no próprio Aerosys. O codusu escolhido é
 *  guardado em memória (this.codusuAtual) e enviado em toda chamada que
 *  precisa saber quem está separando (continuar/marcar/trocar-form/dividir).
 *
 *  DIVIDIR SEPARAÇÃO: mesmo fluxo/lógica do botão "Dividir" da versão
 *  Sankhya (ver EstoqueSeparacaoItensComponent) — o separador atual busca até
 *  2 usuários adicionais (mesma lista de usuários do Aerosys usada na
 *  identificação) e confirma; o backend do Aerosys reparte os itens ainda
 *  pendentes em partes iguais (round-robin) entre os 3 e cria uma sessão de
 *  separação (GPL_SEPARACAO_LEGADO) pra cada separador novo — ver
 *  SeparacaoLegadoService.dividir() no projeto Aerosys.
 *
 *  Isolamento: este componente é standalone e não é referenciado por nenhuma
 *  tela do fluxo Sankhya — pode ser removido (pasta inteira) sem tocar em
 *  mais nada, junto com o pacote gplseparacao no backend Aerosys e os métodos
 *  *SeparacaoLegado* do AerosysGatewayService/Repository/Component/Controller,
 *  quando o Aerosys for desativado.
 * ============================================================================
 */
@Component({
  selector: 'app-estoque-separacao-itens-legado',
  standalone: true,
  imports: [LoadingIndicatorComponent, CommonModule, FormsModule],
  templateUrl: './estoque-separacao-itens-legado.component.html',
  styleUrls: ['./estoque-separacao-itens-legado.component.css']
})
export class EstoqueSeparacaoItensLegadoComponent implements OnInit {
  @Input() idVenda!: number;
  @Input() numVenda?: string;
  @Output() voltar = new EventEmitter<void>();
  @Output() finalizado = new EventEmitter<void>();
  /** Campo de busca de usuário do modal de identificação -- recebe foco automaticamente assim que a TAG é completada (ver onTagInputChange). */
  @ViewChild('buscaUsuarioInput') buscaUsuarioInputRef?: ElementRef<HTMLInputElement>;

  itens: AerosysItemSeparacaoLegado[] = [];
  carregando = false;
  erro = '';
  erroSalvar = '';

  abaAtiva: 'pendentes' | 'separados' = 'pendentes';

  finalizando = false;
  erroFinalizar = '';
  modalFinalizar = false;

  // ---------------------------------------------------------------------
  // Identificação "quem sou você" — TAG + usuário do Aerosys. Precisa ser
  // resolvida ANTES de carregar os itens (ver ngOnInit/confirmarIdentificacao).
  // ---------------------------------------------------------------------
  identificado = false;
  identificando = false;
  /** true enquanto tenta reaproveitar em silêncio uma identificação já feita antes NESTA venda (ver ngOnInit/_restaurarIdentificacao). */
  restaurandoIdentificacao = false;
  tag = '';
  buscaUsuario = '';
  usuariosSugeridos: AerosysUsuarioSeparador[] = [];
  buscandoUsuarios = false;
  usuarioEscolhido: AerosysUsuarioSeparador | null = null;
  erroIdentificacao = '';
  codusuAtual: number | null = null;
  nomeAtual: string | null = null;
  private buscaUsuarioTimer: any;

  /** Só o primeiro nome -- pro badge do header caber na mesma linha ao lado
   *  da TAG (o nome completo é mostrado no title/tooltip e no texto do
   *  modal Dividir, que tem espaço de sobra). */
  primeiroNome(nomeCompleto: string | null): string {
    if (!nomeCompleto) return '';
    return nomeCompleto.trim().split(/\s+/)[0];
  }

  // Modal Trocar Form (e serial, quando a peça controla série) — fluxo de 2
  // passos: escolher o LOTE (form fixo daquele lote) e, se controlaSerial,
  // escolher o serial físico dentro dele.
  modalTrocarForm = false;
  itemTrocando: AerosysItemSeparacaoLegado | null = null;
  passoTrocar: 'lotes' | 'seriais' = 'lotes';
  lotesDisponiveis: AerosysLoteDisponivel[] = [];
  buscandoLotes = false;
  loteEscolhido: AerosysLoteDisponivel | null = null;
  seriaisDoLote: AerosysSerialDisponivel[] = [];
  buscandoSeriais = false;
  trocando = false;
  erroTrocar = '';

  // ---------------------------------------------------------------------
  // Modal Dividir Separação — mesmo padrão do botão Dividir da versão
  // Sankhya: busca até 2 separadores adicionais (lista de usuários do
  // Aerosys) e confirma.
  // ---------------------------------------------------------------------
  modalDividir = false;
  buscaUsuarioDividir = '';
  usuariosSugeridosDividir: AerosysUsuarioSeparador[] = [];
  buscandoUsuariosDividir = false;
  selecionadosDividir: AerosysUsuarioSeparador[] = [];
  dividindo = false;
  erroDividir = '';
  private buscaUsuarioDividirTimer: any;

  // ---------------------------------------------------------------------
  // Modal Transferir Separação — mesmo padrão do botão Transferir da versão
  // Sankhya: busca 1 separador (lista de usuários do Aerosys) e confirma;
  // depois de transferir, o chamador não é mais o separador desta venda.
  // ---------------------------------------------------------------------
  modalTransferir = false;
  buscaUsuarioTransferir = '';
  usuariosSugeridosTransferir: AerosysUsuarioSeparador[] = [];
  buscandoUsuariosTransferir = false;
  usuarioEscolhidoTransferir: AerosysUsuarioSeparador | null = null;
  transferindo = false;
  erroTransferir = '';
  private buscaUsuarioTransferirTimer: any;

  // ---------------------------------------------------------------------
  // Modal Observação -- campo livre POR ITEM pra o separador anotar
  // problemas (ex.: "não tem o item", "quantidade encontrada é menor").
  // Não é geral da venda -- cada item tem a sua (ver
  // SeparacaoItensLegado.observacao no Aerosys). itemObservando guarda qual
  // item está sendo editado no modal.
  // ---------------------------------------------------------------------
  itemObservando: AerosysItemSeparacaoLegado | null = null;
  modalObservacao = false;
  observacaoRascunho = '';
  salvandoObservacao = false;
  erroObservacao = '';

  // ---------------------------------------------------------------------
  // Modal "Item no outro Galpão" -- sinaliza um item como pendente (não
  // separado, mas também não trava mais a fila -- ver itensPendentes/
  // allDone abaixo). Item sinalizado sai da fila "A Separar" e passa a
  // aparecer na aba "Separados" com um badge próprio + botão Desfazer (ver
  // desfazerPendencia). "Resolver" a pendência na tela global "Itens
  // Pendentes" NÃO marca o item como separado -- só fecha a pendência e
  // devolve o item pra fila "A Separar" (aba "pendentes") desta mesma tela,
  // pra o separador conferir/bipar de verdade antes de conseguir concluir
  // (ver allDone, agora estrito). É esse retorno que dispara o "sininho" na
  // tela de Aplicativo de Separação.
  // ---------------------------------------------------------------------
  itemPendencia: AerosysItemSeparacaoLegado | null = null;
  modalPendencia = false;
  pendenciaObservacaoRascunho = '';
  marcandoPendente = false;
  erroPendencia = '';
  desfazendoPendenciaIvdId: number | null = null;

  constructor(private gateway: AerosysGatewayService) {}

  ngOnInit(): void {
    // Se essa MESMA venda já foi identificada antes neste navegador (ver
    // _salvarIdentificacao), reaproveita em silêncio em vez de perguntar de
    // novo -- a sessão já existe no Aerosys (GPL_SEPARACAO_LEGADO), então
    // reenviar o mesmo codusu/tag só confirma a sessão existente, sem
    // revalidar a TAG (ver SeparacaoLegadoService.garantirSessaoAtiva). Se a
    // tela for recriada pelo *ngIf ao navegar de volta pra essa venda, cai
    // direto nos itens sem passar pelo modal.
    const lembrada = this._restaurarIdentificacao();
    if (lembrada) {
      this.restaurandoIdentificacao = true;
      this.tag = lembrada.tag;
      this._continuar(lembrada.codusu, lembrada.nome, lembrada.tag, false);
      return;
    }
    // Sem identificação lembrada -- mostra o modal "quem sou você" (ver
    // confirmarIdentificacao()). A lista de usuários só é buscada conforme o
    // separador digita (ver onBuscaUsuarioChange), igual ao padrão usado nos
    // outros pickers de usuário do sistema.
  }

  /**
   * Extrai uma mensagem de erro legível de uma resposta HTTP do gateway.
   * O corpo do erro pode vir em formatos diferentes dependendo de onde a
   * falha aconteceu: string simples (erro tratado no controller do Aerosys),
   * {erro: "..."} (alguns endpoints antigos do GPL-Integracao) ou ProblemDetail
   * {detail: "..."} (falha de comunicação/autenticação capturada pelo
   * ApiExceptionHandler do GPL-Integracao, ex.: Aerosys fora do ar, endpoint
   * novo não implantado ainda, etc.).
   */
  private extrairErro(err: any, fallback: string): string {
    const corpo = err?.error;
    if (typeof corpo === 'string' && corpo.trim()) return corpo;
    if (corpo?.erro) return corpo.erro;
    if (corpo?.detail) return corpo.detail;
    if (corpo?.message) return corpo.message;
    if (err?.status === 0) return 'Não foi possível conectar ao servidor.';
    return err?.message ?? fallback;
  }

  // =======================================================================
  // Identificação "quem sou você"
  // =======================================================================

  onBuscaUsuarioChange(): void {
    clearTimeout(this.buscaUsuarioTimer);
    const termo = this.buscaUsuario.trim();
    if (termo.length < 2) {
      this.usuariosSugeridos = [];
      this.buscandoUsuarios = false;
      return;
    }
    this.buscaUsuarioTimer = setTimeout(() => this.buscarUsuarios(termo), 300);
  }

  private buscarUsuarios(termo: string): void {
    this.buscandoUsuarios = true;
    this.erroIdentificacao = '';
    this.gateway.buscarUsuariosSeparacaoLegado(termo).subscribe({
      next: (usuarios) => {
        this.usuariosSugeridos = usuarios ?? [];
        this.buscandoUsuarios = false;
      },
      error: (err: any) => {
        this.usuariosSugeridos = [];
        this.buscandoUsuarios = false;
        this.erroIdentificacao = this.extrairErro(err, 'Erro ao buscar usuários do Aerosys.');
      }
    });
  }

  escolherUsuario(usuario: AerosysUsuarioSeparador): void {
    this.usuarioEscolhido = usuario;
    this.buscaUsuario = usuario.nome ?? '';
    this.usuariosSugeridos = [];
  }

  limparUsuarioEscolhido(): void {
    this.usuarioEscolhido = null;
    this.buscaUsuario = '';
    this.usuariosSugeridos = [];
  }

  /** Mantém só dígitos e no máximo 2 caracteres — TAGs vão de 01 a 40 (mesmo padrão do modal de TAG do Sankhya, ver estoque-separacao.component). */
  onTagInputChange(valor: string): void {
    const limpo = (valor || '').replace(/\D/g, '').slice(0, 2);
    const completou = limpo.length === 2 && limpo !== this.tag;
    if (limpo !== this.tag) {
      this.tag = limpo;
    }
    if (this.erroIdentificacao) this.erroIdentificacao = '';
    // TAG completa (2 dígitos) -- pula direto pro campo de usuário, sem
    // precisar de Tab/clique manual (o separador já sabe seu usuário de cor,
    // só precisa digitar; a lista de sugestões aparece ao digitar ali).
    if (completou && !this.usuarioEscolhido) {
      setTimeout(() => this.buscaUsuarioInputRef?.nativeElement?.focus(), 0);
    }
  }

  /**
   * Valida localmente (formato) e só fecha o modal se o Aerosys aceitar a TAG --
   * a validação de verdade (obrigatória, 01-40, e não em uso por OUTRA venda
   * com separação ainda ativa) é a mesma regra do Sankhya, feita no backend
   * (ver SeparacaoLegadoService.validarTag, projeto Aerosys) e devolvida aqui
   * como erro se falhar, sem fechar a identificação.
   */
  confirmarIdentificacao(): void {
    this.erroIdentificacao = '';
    if (!this.usuarioEscolhido) {
      this.erroIdentificacao = 'Selecione o seu usuário na lista antes de continuar.';
      return;
    }
    const tagLimpa = (this.tag || '').trim();
    if (!tagLimpa) {
      this.erroIdentificacao = 'Informe a TAG antes de continuar.';
      return;
    }
    const tagNum = Number(tagLimpa);
    if (!Number.isInteger(tagNum) || tagNum < 1 || tagNum > 40) {
      this.erroIdentificacao = 'TAG inválida — informe um número entre 01 e 40.';
      return;
    }
    this.identificando = true;
    this._continuar(this.usuarioEscolhido.codusu, this.usuarioEscolhido.nome ?? null, tagLimpa, true);
  }

  /**
   * Chama o "continuar" de verdade (mesmo endpoint usado pelo submit manual
   * do modal e pela restauração silenciosa em ngOnInit) e, se pedido,
   * lembra a identificação (codusu/nome/tag) pra essa venda no localStorage
   * -- é assim que "sair e voltar não pede de novo" funciona.
   */
  private _continuar(codusu: number, nome: string | null, tag: string, persistir: boolean): void {
    this.gateway.continuarSeparacaoLegado(this.idVenda, codusu, tag).subscribe({
      next: (itens) => {
        this.identificando = false;
        this.restaurandoIdentificacao = false;
        this.codusuAtual = codusu;
        this.nomeAtual = nome;
        this.identificado = true;
        this.itens = itens ?? [];
        if (persistir) this._salvarIdentificacao(codusu, nome, tag);
      },
      error: (err: any) => {
        this.identificando = false;
        if (this.restaurandoIdentificacao) {
          // Identificação lembrada não colou mais (ex.: navegador trocou de
          // usuário, ou algo mudou do lado do Aerosys) -- não trava a tela
          // com um erro que o separador não pediu: descarta o que tava
          // guardado e volta pro modal normal, como se fosse a 1ª vez.
          this.restaurandoIdentificacao = false;
          this._removerIdentificacaoLembrada();
          this.tag = '';
          return;
        }
        this.erroIdentificacao = this.extrairErro(err, 'Erro ao continuar a separação.');
      }
    });
  }

  /** Esquece a identificação lembrada desta venda e volta a pedir TAG + usuário -- "não sou eu" / troca de turno. */
  trocarIdentificacao(): void {
    this._removerIdentificacaoLembrada();
    this.identificado = false;
    this.codusuAtual = null;
    this.nomeAtual = null;
    this.usuarioEscolhido = null;
    this.buscaUsuario = '';
    this.tag = '';
    this.itens = [];
  }

  private _storageKeyIdentificacao(): string {
    return `gpl_estoque_separacao_legado_identificacao_${this.idVenda}`;
  }

  private _salvarIdentificacao(codusu: number, nome: string | null, tag: string): void {
    try {
      localStorage.setItem(this._storageKeyIdentificacao(), JSON.stringify({ codusu, nome, tag }));
    } catch {
      // localStorage indisponível (ex.: navegação privada) -- só perde a conveniência de não perguntar de novo, nada quebra.
    }
  }

  private _restaurarIdentificacao(): { codusu: number; nome: string | null; tag: string } | null {
    try {
      const raw = localStorage.getItem(this._storageKeyIdentificacao());
      if (!raw) return null;
      const obj = JSON.parse(raw);
      if (typeof obj?.codusu !== 'number' || typeof obj?.tag !== 'string' || !obj.tag) return null;
      return { codusu: obj.codusu, nome: obj.nome ?? null, tag: obj.tag };
    } catch {
      return null;
    }
  }

  private _removerIdentificacaoLembrada(): void {
    try {
      localStorage.removeItem(this._storageKeyIdentificacao());
    } catch {
      // ignora
    }
  }

  // =======================================================================
  // Itens da separação
  // =======================================================================

  carregarItens(): void {
    if (this.codusuAtual == null) return;
    this.carregando = true;
    this.erro = '';
    this.gateway.continuarSeparacaoLegado(this.idVenda, this.codusuAtual, this.tag).subscribe({
      next: (itens) => {
        this.itens = itens ?? [];
        this.carregando = false;
      },
      error: (err: any) => {
        this.erro = this.extrairErro(err, 'Erro ao carregar itens da separação.');
        this.carregando = false;
      }
    });
  }

  onVoltar(): void {
    this.voltar.emit();
  }

  toggleItem(item: AerosysItemSeparacaoLegado): void {
    if (this.codusuAtual == null) return;
    const novoEstado = item.separado !== 'S';
    this.erroSalvar = '';
    this.gateway.marcarItemSeparacaoLegado(this.idVenda, item.ivdId, novoEstado, this.codusuAtual).subscribe({
      next: (itemAtualizado) => {
        this._substituirItemNaLista(itemAtualizado);
        if (novoEstado && this.itensPendentes.length === 0) {
          this.abaAtiva = 'separados';
          if (this.allDone) {
            this.modalFinalizar = true;
          }
        }
        if (!novoEstado && this.abaAtiva === 'separados') {
          this.abaAtiva = 'pendentes';
        }
      },
      error: (err: any) => {
        this.erroSalvar = this.extrairErro(err, 'Erro ao salvar item.');
      }
    });
  }

  /**
   * Substitui só 1 item na lista já carregada, pelo ivdId -- marcarItem e
   * salvarObservacaoItem agora devolvem só o item que mudou (não a venda
   * inteira, ver comentário em SeparacaoLegadoService.marcarItem no
   * Aerosys), então não tem mais lista completa pra "trocar" a cada clique,
   * só atualiza a entrada correspondente na que já está em memória.
   */
  private _substituirItemNaLista(itemAtualizado: AerosysItemSeparacaoLegado): void {
    if (!itemAtualizado) return;
    this.itens = this.itens.map(i => (i.ivdId === itemAtualizado.ivdId ? itemAtualizado : i));
  }

  /**
   * Fila "A Separar" -- itens ainda não separados E sem pendência aberta.
   * Um item sinalizado como "Item no outro Galpão" (ver marcarPendente) sai
   * daqui (não trava mais a fila, o separador segue pro próximo) mesmo sem
   * estar SEPARADO='S' -- ele passa a aparecer na aba "Separados" com um
   * badge próprio (ver itensSeparadosLista).
   */
  get itensPendentes(): AerosysItemSeparacaoLegado[] {
    return this.itens.filter(i => i.separado !== 'S' && !i.pendente);
  }

  /**
   * A aba "A Separar" mostra só o PRÓXIMO item pendente (não a lista inteira
   * de uma vez) -- pedido do cliente pra o separador ir item a item, sem se
   * distrair com o resto da lista. Ao marcar o check, o item sai de
   * itensPendentes e este getter automaticamente "revela" o próximo. O
   * contador da aba e a barra de progresso continuam usando itensPendentes
   * (lista completa) normalmente -- só a renderização do card é que fica
   * limitada a 1 item por vez.
   */
  get itemPendenteAtual(): AerosysItemSeparacaoLegado[] {
    const pendentes = this.itensPendentes;
    return pendentes.length > 0 ? [pendentes[0]] : [];
  }

  /** Itens efetivamente separados OU sinalizados como pendentes ("Item no outro Galpão") -- ambos saem da fila "A Separar". */
  get itensSeparadosLista(): AerosysItemSeparacaoLegado[] {
    return this.itens.filter(i => i.separado === 'S' || i.pendente);
  }

  get totalSeparados(): number {
    return this.itens.filter(i => i.separado === 'S').length;
  }

  get totalPendentesAbertas(): number {
    return this.itens.filter(i => !!i.pendente).length;
  }

  get progresso(): number {
    if (!this.itens.length) return 0;
    return Math.round((this.totalSeparados / this.itens.length) * 100);
  }

  /**
   * "Tudo pronto pra concluir" -- ESTRITO: todo item precisa estar
   * SEPARADO='S' de verdade. Itens sinalizados como pendentes ("Item no
   * outro Galpão") NÃO contam, mesmo depois de resolvidos na tela global
   * "Itens Pendentes" -- resolver só devolve o item pra fila "A Separar"
   * (ver resolverPendencia no Aerosys), o separador ainda precisa
   * conferir/marcar ele aqui. Decisão explícita do cliente: a venda tem que
   * continuar com VEN_STATUS = "SEPARAÇÃO" (não finalizar) enquanto existir
   * qualquer pendência em aberto ou recém-resolvida sem confirmação.
   */
  get allDone(): boolean {
    return this.itens.length > 0 && this.itens.every(i => i.separado === 'S');
  }

  finalizarDoModal(): void {
    this.modalFinalizar = false;
    this.finalizar();
  }

  finalizar(): void {
    if (this.finalizando) return;
    this.finalizando = true;
    this.erroFinalizar = '';
    this.gateway.finalizarSeparacaoLegado(this.idVenda).subscribe({
      next: () => {
        this.finalizando = false;
        this.finalizado.emit();
      },
      error: (err: any) => {
        this.finalizando = false;
        this.erroFinalizar = this.extrairErro(err, 'Falha ao finalizar separação.');
      }
    });
  }

  // =======================================================================
  // Troca manual de form/serial
  // =======================================================================

  /** Abre o modal de troca manual, já carregando os lotes disponíveis (passo 1). */
  abrirModalTrocarForm(item: AerosysItemSeparacaoLegado, event: Event): void {
    event.stopPropagation();
    this.itemTrocando = item;
    this.passoTrocar = 'lotes';
    this.lotesDisponiveis = [];
    this.loteEscolhido = null;
    this.seriaisDoLote = [];
    this.erroTrocar = '';
    this.modalTrocarForm = true;
    this.buscandoLotes = true;
    this.gateway.lotesDisponiveisSeparacaoLegado(this.idVenda, item.ivdId).subscribe({
      next: (lotes) => {
        this.lotesDisponiveis = lotes ?? [];
        this.buscandoLotes = false;
      },
      error: (err: any) => {
        this.erroTrocar = this.extrairErro(err, 'Não foi possível carregar os lotes disponíveis.');
        this.buscandoLotes = false;
      }
    });
  }

  fecharModalTrocarForm(): void {
    this.modalTrocarForm = false;
    this.itemTrocando = null;
    this.passoTrocar = 'lotes';
    this.lotesDisponiveis = [];
    this.loteEscolhido = null;
    this.seriaisDoLote = [];
    this.erroTrocar = '';
  }

  /** Escolhe um lote: se a peça controla série, avança pro passo de escolher o serial; senão, já troca direto. */
  escolherLote(lote: AerosysLoteDisponivel): void {
    if (!this.itemTrocando || this.trocando) return;
    if (!this.itemTrocando.controlaSerial) {
      this.confirmarTroca(lote.icoId, null);
      return;
    }
    this.loteEscolhido = lote;
    this.passoTrocar = 'seriais';
    this.seriaisDoLote = [];
    this.erroTrocar = '';
    this.buscandoSeriais = true;
    this.gateway.seriaisDoLoteSeparacaoLegado(this.idVenda, this.itemTrocando.ivdId, lote.icoId).subscribe({
      next: (seriais) => {
        this.seriaisDoLote = seriais ?? [];
        this.buscandoSeriais = false;
      },
      error: (err: any) => {
        this.erroTrocar = this.extrairErro(err, 'Não foi possível carregar os seriais do lote.');
        this.buscandoSeriais = false;
      }
    });
  }

  /** Volta do passo "seriais" pro passo "lotes", sem fechar o modal. */
  voltarParaLotes(): void {
    this.passoTrocar = 'lotes';
    this.loteEscolhido = null;
    this.seriaisDoLote = [];
    this.erroTrocar = '';
  }

  escolherSerial(serial: AerosysSerialDisponivel): void {
    if (!this.loteEscolhido) return;
    this.confirmarTroca(this.loteEscolhido.icoId, serial.icsId);
  }

  private confirmarTroca(icoId: number, icsId: number | null): void {
    if (!this.itemTrocando || this.trocando || this.codusuAtual == null) return;
    const item = this.itemTrocando;
    this.trocando = true;
    this.erroTrocar = '';
    this.gateway.trocarFormSeparacaoLegado(this.idVenda, item.ivdId, icoId, icsId, this.codusuAtual).subscribe({
      next: (itensAtualizados) => {
        this.itens = itensAtualizados ?? this.itens;
        this.trocando = false;
        this.fecharModalTrocarForm();
      },
      error: (err: any) => {
        this.trocando = false;
        this.erroTrocar = this.extrairErro(err, 'Erro ao trocar form/serial.');
      }
    });
  }

  // =======================================================================
  // Dividir Separação
  // =======================================================================

  abrirModalDividir(): void {
    this.modalDividir = true;
    this.buscaUsuarioDividir = '';
    this.usuariosSugeridosDividir = [];
    this.selecionadosDividir = [];
    this.erroDividir = '';
    this.buscarUsuariosDividir('');
  }

  fecharModalDividir(): void {
    if (this.dividindo) return;
    this.modalDividir = false;
    this.buscaUsuarioDividir = '';
    this.usuariosSugeridosDividir = [];
    this.selecionadosDividir = [];
    this.erroDividir = '';
  }

  onBuscaUsuarioDividirChange(): void {
    clearTimeout(this.buscaUsuarioDividirTimer);
    this.buscaUsuarioDividirTimer = setTimeout(() => this.buscarUsuariosDividir(this.buscaUsuarioDividir.trim()), 300);
  }

  private buscarUsuariosDividir(termo: string): void {
    this.buscandoUsuariosDividir = true;
    this.erroDividir = '';
    this.gateway.buscarUsuariosSeparacaoLegado(termo).subscribe({
      next: (usuarios) => {
        const jaEscolhidos = new Set(this.selecionadosDividir.map(u => u.codusu));
        this.usuariosSugeridosDividir = (usuarios ?? []).filter(
          u => u.codusu !== this.codusuAtual && !jaEscolhidos.has(u.codusu)
        );
        this.buscandoUsuariosDividir = false;
      },
      error: (err: any) => {
        this.usuariosSugeridosDividir = [];
        this.buscandoUsuariosDividir = false;
        this.erroDividir = this.extrairErro(err, 'Erro ao buscar usuários do Aerosys.');
      }
    });
  }

  adicionarSeparadorDividir(usuario: AerosysUsuarioSeparador): void {
    if (this.selecionadosDividir.length >= 2) return;
    this.selecionadosDividir.push(usuario);
    this.buscaUsuarioDividir = '';
    if (this.selecionadosDividir.length < 2) {
      this.buscarUsuariosDividir('');
    } else {
      this.usuariosSugeridosDividir = [];
    }
  }

  removerSeparadorDividir(usuario: AerosysUsuarioSeparador): void {
    this.selecionadosDividir = this.selecionadosDividir.filter(u => u.codusu !== usuario.codusu);
  }

  confirmarDivisao(): void {
    if (this.dividindo || this.codusuAtual == null) return;
    if (this.selecionadosDividir.length === 0) {
      this.erroDividir = 'Escolha pelo menos 1 separador pra dividir.';
      return;
    }
    this.dividindo = true;
    this.erroDividir = '';
    const novosCodusu = this.selecionadosDividir.map(u => u.codusu);
    this.gateway.dividirSeparacaoLegado(this.idVenda, this.codusuAtual, novosCodusu).subscribe({
      next: (itensAtualizados) => {
        this.itens = itensAtualizados ?? this.itens;
        this.dividindo = false;
        this.modalDividir = false;
        this.buscaUsuarioDividir = '';
        this.usuariosSugeridosDividir = [];
        this.selecionadosDividir = [];
      },
      error: (err: any) => {
        this.dividindo = false;
        this.erroDividir = this.extrairErro(err, 'Erro ao dividir a separação.');
      }
    });
  }

  // =======================================================================
  // Transferir Separação
  // =======================================================================

  abrirModalTransferir(): void {
    this.modalTransferir = true;
    this.buscaUsuarioTransferir = '';
    this.usuariosSugeridosTransferir = [];
    this.usuarioEscolhidoTransferir = null;
    this.erroTransferir = '';
    this.buscarUsuariosTransferir('');
  }

  fecharModalTransferir(): void {
    if (this.transferindo) return;
    this.modalTransferir = false;
    this.buscaUsuarioTransferir = '';
    this.usuariosSugeridosTransferir = [];
    this.usuarioEscolhidoTransferir = null;
    this.erroTransferir = '';
  }

  onBuscaUsuarioTransferirChange(): void {
    clearTimeout(this.buscaUsuarioTransferirTimer);
    this.buscaUsuarioTransferirTimer = setTimeout(() => this.buscarUsuariosTransferir(this.buscaUsuarioTransferir.trim()), 300);
  }

  private buscarUsuariosTransferir(termo: string): void {
    this.buscandoUsuariosTransferir = true;
    this.erroTransferir = '';
    this.gateway.buscarUsuariosSeparacaoLegado(termo).subscribe({
      next: (usuarios) => {
        this.usuariosSugeridosTransferir = (usuarios ?? []).filter(u => u.codusu !== this.codusuAtual);
        this.buscandoUsuariosTransferir = false;
      },
      error: (err: any) => {
        this.usuariosSugeridosTransferir = [];
        this.buscandoUsuariosTransferir = false;
        this.erroTransferir = this.extrairErro(err, 'Erro ao buscar usuários do Aerosys.');
      }
    });
  }

  escolherUsuarioTransferir(usuario: AerosysUsuarioSeparador): void {
    this.usuarioEscolhidoTransferir = usuario;
    this.buscaUsuarioTransferir = usuario.nome ?? '';
    this.usuariosSugeridosTransferir = [];
  }

  limparUsuarioEscolhidoTransferir(): void {
    this.usuarioEscolhidoTransferir = null;
    this.buscaUsuarioTransferir = '';
    this.buscarUsuariosTransferir('');
  }

  confirmarTransferencia(): void {
    if (this.transferindo || this.codusuAtual == null || !this.usuarioEscolhidoTransferir) return;
    this.transferindo = true;
    this.erroTransferir = '';
    this.gateway.transferirSeparacaoLegado(this.idVenda, this.codusuAtual, this.usuarioEscolhidoTransferir.codusu).subscribe({
      next: () => {
        this.transferindo = false;
        this.modalTransferir = false;
        this.voltar.emit(); // não é mais o separador desta venda
      },
      error: (err: any) => {
        this.transferindo = false;
        this.erroTransferir = this.extrairErro(err, 'Falha ao transferir separação.');
      }
    });
  }

  // =======================================================================
  // Observação POR ITEM (não geral da venda -- ver comentário no bloco de estado acima)
  // =======================================================================

  /** item.observacao já vem preenchido em cada item na lista (ver ItemSeparacaoLegadoDTO) -- não precisa buscar à parte. */
  abrirModalObservacaoItem(item: AerosysItemSeparacaoLegado, event: Event): void {
    event.stopPropagation(); // não deixa o click "vazar" pro toggleItem do card
    this.itemObservando = item;
    this.observacaoRascunho = item.observacao ?? '';
    this.erroObservacao = '';
    this.modalObservacao = true;
  }

  fecharModalObservacao(): void {
    if (this.salvandoObservacao) return;
    this.modalObservacao = false;
    this.itemObservando = null;
  }

  salvarObservacaoItem(): void {
    if (this.salvandoObservacao || this.codusuAtual == null || !this.itemObservando) return;
    this.salvandoObservacao = true;
    this.erroObservacao = '';
    this.gateway.salvarObservacaoItemSeparacaoLegado(this.idVenda, this.itemObservando.ivdId, this.observacaoRascunho.trim(), this.codusuAtual).subscribe({
      next: (itemAtualizado) => {
        this._substituirItemNaLista(itemAtualizado);
        this.salvandoObservacao = false;
        this.modalObservacao = false;
        this.itemObservando = null;
      },
      error: (err: any) => {
        this.salvandoObservacao = false;
        this.erroObservacao = this.extrairErro(err, 'Erro ao salvar observação.');
      }
    });
  }

  // =======================================================================
  // "Item no outro Galpão" -- sinalizar/desfazer pendência (ver comentário
  // no bloco de estado acima). Resolver de verdade (marcar como
  // efetivamente separado) é feito na tela global "Itens Pendentes", não
  // aqui.
  // =======================================================================

  abrirModalPendencia(item: AerosysItemSeparacaoLegado, event: Event): void {
    event.stopPropagation(); // não deixa o click "vazar" pro toggleItem do card
    this.itemPendencia = item;
    this.pendenciaObservacaoRascunho = '';
    this.erroPendencia = '';
    this.modalPendencia = true;
  }

  fecharModalPendencia(): void {
    if (this.marcandoPendente) return;
    this.modalPendencia = false;
    this.itemPendencia = null;
    this.pendenciaObservacaoRascunho = '';
  }

  confirmarPendencia(): void {
    if (this.marcandoPendente || this.codusuAtual == null || !this.itemPendencia) return;
    this.marcandoPendente = true;
    this.erroPendencia = '';
    const texto = this.pendenciaObservacaoRascunho.trim();
    this.gateway.marcarItemPendenteSeparacaoLegado(this.idVenda, this.itemPendencia.ivdId, this.codusuAtual, texto || null).subscribe({
      next: (itemAtualizado) => {
        this._substituirItemNaLista(itemAtualizado);
        this.marcandoPendente = false;
        this.modalPendencia = false;
        this.itemPendencia = null;
        this.pendenciaObservacaoRascunho = '';
        // Mesmo comportamento de toggleItem: sinalizar o último item pendente
        // "revela" a aba Separados e, se completou tudo (separados + pendências), oferece Concluir.
        if (this.itensPendentes.length === 0) {
          this.abaAtiva = 'separados';
          if (this.allDone) {
            this.modalFinalizar = true;
          }
        }
      },
      error: (err: any) => {
        this.marcandoPendente = false;
        this.erroPendencia = this.extrairErro(err, 'Erro ao sinalizar pendência.');
      }
    });
  }

  /** Desfaz uma sinalização feita por engano -- item volta pra fila "A Separar" (SEPARADO continua 'N', nunca chegou a mudar). */
  desfazerPendencia(item: AerosysItemSeparacaoLegado, event: Event): void {
    event.stopPropagation();
    if (this.desfazendoPendenciaIvdId != null) return;
    this.desfazendoPendenciaIvdId = item.ivdId;
    this.erroSalvar = '';
    this.gateway.desfazerPendenciaSeparacaoLegado(this.idVenda, item.ivdId).subscribe({
      next: (itemAtualizado) => {
        this._substituirItemNaLista(itemAtualizado);
        this.desfazendoPendenciaIvdId = null;
        if (this.abaAtiva === 'separados' && this.itensPendentes.length > 0) {
          this.abaAtiva = 'pendentes';
        }
      },
      error: (err: any) => {
        this.desfazendoPendenciaIvdId = null;
        this.erroSalvar = this.extrairErro(err, 'Erro ao desfazer pendência.');
      }
    });
  }
}
